// views/NetworkedGameView.jsx: M4-11 server-authoritative table view.
//
// Drives the table from a server snapshot delivered over the M3-13 WS
// service. The view never recomputes game logic, it trusts the snapshot
// wholesale (per the M4 invariant in plan §M4-10).
//
// Restyle step 2 (2026-10-11): the cream table from the chosen mockup.
// A top bar (two score blocks, the status row, a menu button) sits over
// a board laid out by canvas/cream-layout.js: a 2x2 block of trick cells
// with a seat tag beside each, a controls row, and your hand. The canvas
// (canvas/cream-board.js) draws the felt and the cards; DOM draws every
// word (ui/CreamTable.jsx). Game log, settings, how to play and leave
// live in the menu.

import '../styles/room.css';
import '../styles/networked-game.css';
import '../styles/fonts.css';
import '../styles/cream-table.css';
import '../styles/cream-game-panels.css';
import { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback, useContext } from 'preact/hooks';
import { readDevicePixelRatio, watchDevicePixelRatio, prepareHiDpiCanvas, backingSize, keepStageAligned } from '../canvas/hidpi.js';
import { creamLayout, handCardRects, seatsBySlot, slotOfSeat, shortName } from '../canvas/cream-layout.js';
import { renderCreamBoard, HOVER_LIFT } from '../canvas/cream-board.js';
import { describeAriaLabel } from '../canvas/table.js';
import { useGameWs } from '../hooks/useGameWs.js';
import { useClockOffset } from '../hooks/useClockOffset.js';
import { createCardSnapDetector } from '../ui/card-snap.js';
import { alignCellNames } from '../ui/cell-name-snap.js';
import { createTrickResolutionDetector, trickWonEventFor, teamOfSeatLua } from '../ui/trick-resolution-detector.js';
import { getParticleLayer } from '../ui/particle-layer-singleton.js';
import { usePrefersReducedMotion } from '../ui/useTrickAnimation.js';
import { useCardPlayAnimation } from '../ui/useCardPlayAnimation.js';
import { useTrickSequence } from '../ui/useTrickSequence.js';
import { _internals as trickSeqInternals } from '../ui/trick-sequencer.js';
import { createTrumpDecidedDetector } from '../ui/createTrumpDecidedDetectorOrReduced.js';
import { HOLD_MS as SPLASH_HOLD_MS, FADE_MS as SPLASH_FADE_MS, makerLineFor } from '../ui/trump-splash-logic.js';
import { EndOfHandModal } from '../ui/EndOfHandModal.jsx';
import { GameOverOverlay } from '../ui/GameOverOverlay.jsx';
import { ReadyCountdown } from '../ui/ReadyCountdown.jsx';
import { waitingForText, canShowRecap } from '../ui/end-of-hand.js';
import { deriveWinner } from '../ui/game-over.js';
import { networkedStatusText } from './networked-status.js';
import { handSummaryFromSnapshot } from '../ui/hand-summary.js';
import { normalizeSnapshotShape } from './snapshot-shape.js';
import { seatLabelsFromSnapshot, plainSeatLabelsFromSnapshot } from './seat-labels.js';
import { sitOutNoteText } from './loner-note.js';
import { reconnectBannerText, leaveConfirmText, ownSeatBotNote, onceOnly, leaveConfirmKeyAction, moveLockText, handCardHitState, upcardHitState, readyButtonState, connectErrorBannerText, SENDING_TEXT } from './networked-flow.js';
import {
  tagContents, turnChipText, hintText, scoreModel, handNumber, lastTrick, cellLabel, passedSeats,
  SUIT_WORD, asList,
} from './cream-table-model.js';
import * as audio from '../audio/events.js';
import { EVENTS } from '../audio/events.js';
import { IdentityBadge } from '../components/IdentityBadge.jsx';
import { EventLogPanel } from '../components/EventLogPanel.jsx';
import { OverlayNavContext } from '../nav/HowToPlayTrigger.jsx';
import { ReconnectModal } from '../components/ReconnectModal.jsx';
import { illegalPlayMessage } from '../ui/illegal-play.js';
import { cardTapOutcome, applySelection, selectionSyncEffect } from '../ui/card-selection.js';
import { describeCard } from '../canvas/cards.js';
import { dealerPickupCaption } from '../canvas/upcard.js';
import { dealerContextLine } from '../ui/bidding-logic.js';
import { SettingsPanel } from '../ui/SettingsPanel.jsx';
import { useSettings } from '../ui/useSettings.js';
import {
  ScoreBlock, SeatTag, SuitIcon, PlayControls, CreamBidControls, CreamDiscardPrompt, WinBadge,
  TrumpAnnounce, TurnSeconds, useTurnDigits, orderedBidButtons, bidLineText,
} from '../ui/CreamTable.jsx';

/**
 * Convert parity.serialize's Lua-table-as-JSON-object shape into the
 * arrays the canvas helpers expect. Re-exported here so existing
 * importers keep working; the implementation lives in snapshot-shape.js
 * (pure .js, importable from Node tests).
 *
 * @param {any} snap
 * @returns {any}
 */
export { normalizeSnapshotShape };

const SLOTS = /** @type {const} */ (['S', 'W', 'N', 'E']);
// The mockup's plusTrick: "+1 TRICK" shows max(700, sweep + 400) ms.
const PLUS_TRICK_MS = Math.max(700, trickSeqInternals.SWEEP_MS + 400);

/**
 * Where the top bar is crowded. `scores`: the brand, the score blocks,
 * "TO n" or the menu button overlap (or sit closer than 2 px) or leave
 * the bar. `status`: the status row cuts one of its items, or an item
 * cuts its own words.
 *
 * @param {HTMLElement} header
 * @returns {{ scores: boolean, status: boolean, why: string[] }}
 */
function topBarCrowding(header) {
  const box = (e) => e.getBoundingClientRect();
  const shown = (e) => e.getClientRects().length > 0 && getComputedStyle(e).visibility !== 'hidden';
  const name = (e) => String(e.className || e.tagName).split(' ')[0];
  const hb = box(header);
  /** @type {string[]} */
  const why = [];
  const left = [...header.querySelectorAll('.brand, .score-block, .to-target, .menu-button')].filter(shown);
  for (const e of left) {
    const r = box(e);
    if (r.left < hb.left - 0.5 || r.right > hb.right + 0.5) why.push(`${name(e)} leaves the bar`);
  }
  if (header.scrollWidth > header.clientWidth) why.push('the bar scrolls');
  for (let i = 0; i < left.length; i += 1) {
    for (let j = i + 1; j < left.length; j += 1) {
      const a = box(left[i]);
      const b = box(left[j]);
      if (a.left < b.right + 2 && b.left < a.right + 2 && a.top < b.bottom && b.top < a.bottom) why.push(`${name(left[i])} touches ${name(left[j])}`);
    }
  }
  // A score block's own words must fit it too.
  for (const b of header.querySelectorAll('.score-block')) if (b.scrollWidth > b.clientWidth + 0.5) why.push('a score block cuts its words');
  const scoreWhy = why.splice(0);
  // The status row: in the bar, or (landscape, fit-side) beside the board.
  const row = header.closest('.cream-view')?.querySelector('[data-testid="status-row"]') ?? null;
  if (row) {
    const sb = box(row);
    const items = [...row.querySelectorAll('.table-title, .trump-chip, .turn-chip')].filter(shown);
    for (const e of items) {
      const r = box(e);
      if (r.left < sb.left - 0.5 || r.right > sb.right + 0.5 || r.top < sb.top - 0.5 || r.bottom > sb.bottom + 0.5) why.push(`${name(e)} (${Math.round(r.left)},${Math.round(r.top)} ${Math.round(r.width)}x${Math.round(r.height)}) leaves the status row (${Math.round(sb.left)},${Math.round(sb.top)} ${Math.round(sb.width)}x${Math.round(sb.height)})`);
      if (e.scrollWidth > e.clientWidth + 0.5) why.push(`${name(e)} cuts its words`);
    }
    // Two items must not touch, and the status row must not touch the
    // score blocks or the menu.
    const others = [...items, ...(row.closest('.cream-top') ? left : [])];
    for (let i = 0; i < items.length; i += 1) {
      for (let j = i + 1; j < others.length; j += 1) {
        const a = box(items[i]);
        const b = box(others[j]);
        if (a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom) why.push(`${name(items[i])} overlaps ${name(others[j])}`);
      }
    }
  }
  return { scores: scoreWhy.length > 0, status: why.length > 0, why: [...scoreWhy, ...why] };
}

const rectStr = (r) => `${r.x},${r.y},${r.w},${r.h}`;

/**
 * System-font mode: the seat tags' words measured in the system face
 * (tokens.css --cream-system, what body.system-font draws them in), so
 * the layout sizes each tag to hold them. Whole pixels, rounded up.
 *
 * @returns {(text: string, px: number) => number}
 */
function systemTextMeasure() {
  const family = getComputedStyle(document.documentElement).getPropertyValue('--cream-system').trim();
  if (!family) throw new Error('[cream-table] --cream-system is not set: the system-font tags cannot be measured');
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) throw new Error('[cream-table] no canvas 2D context: the system-font tags cannot be measured');
  /** @type {Map<string, number>} */
  const seen = new Map();
  return (text, px) => {
    const key = `${px}|${text}`;
    let w = seen.get(key);
    if (w === undefined) {
      ctx.font = `400 ${px}px ${family}`;
      w = Math.ceil(ctx.measureText(text).width);
      seen.set(key, w);
    }
    return w;
  };
}

// The table's pixel faces (styles/fonts.css).
const PIXEL_FAMILY = /^["']?(Tiny5|Silkscreen)["']?$/;
// font-display: block hides text for about 3 s; past that a browser
// shows the fallback face, so the bar is fitted in it.
const PIXEL_FONT_WAIT_MS = 3_000;
const pixelFaces = () => [...document.fonts].filter((f) => PIXEL_FAMILY.test(f.family));

/** Whether every Tiny5 and Silkscreen face is loaded (true without a font API). */
function pixelFontsLoaded() {
  if (typeof document === 'undefined' || !document.fonts) return true;
  const faces = pixelFaces();
  return faces.length > 0 && faces.every((f) => f.status === 'loaded');
}

/**
 * Load the Tiny5 and Silkscreen faces. Resolves when they are in; if they
 * fail or take over 3 s it names the problem and resolves anyway, and the
 * bar is then fitted in the fallback face.
 *
 * @returns {Promise<void>}
 */
function loadPixelFonts() {
  const faces = pixelFaces();
  if (!faces.length) {
    console.error('[cream-table] no Tiny5 or Silkscreen face is declared: the top bar is fitted in a fallback face');
    return Promise.resolve();
  }
  /** @type {ReturnType<typeof setTimeout>|undefined} */
  let timer;
  const late = new Promise((resolve) => {
    timer = setTimeout(() => resolve('late'), PIXEL_FONT_WAIT_MS);
  });
  return Promise.race([Promise.all(faces.map((f) => f.load())), late]).then((r) => {
    if (r === 'late') console.error(`[cream-table] the pixel fonts did not load in ${PIXEL_FONT_WAIT_MS} ms: the top bar is fitted in a fallback face until they do`);
  }, (err) => {
    console.error(`[cream-table] a pixel font failed to load (${err && err.message ? err.message : String(err)}): the top bar is fitted in a fallback face`);
  }).finally(() => clearTimeout(timer));
}

/**
 * The top bar's steps, in order (cream-table.css fit-*): tighter score
 * blocks, words that may go at any size (the brand, "TO n"), then room found by layout (a second
 * status line, on landscape the status row in the board's right-hand
 * column above the controls row, a long name in the turn chip shortened
 * with a dot as the tags do it), then, on the smallest screens only (under 360x640 and
 * 640x360), the words the owner allows to go: the word TRUMP, the
 * TRICKS word, HAND n. The suit, the scores and the turn chip's words
 * and seconds always stay.
 */
const FIT_STEPS = [
  { id: 'a0', side: 'score' },   // tighter score blocks and gaps (no words go)
  { id: 'a1', side: 'score' },   // the brand
  { id: 'a2', side: 'score' },   // "TO n"
  { id: 'two', side: 'status' }, // the status row takes two lines
  { id: 'side', side: 'status', land: true }, // landscape: into the side column
  { id: 'name', side: 'status' }, // the turn chip's name, shortened
  { id: 'b4', side: 'status' },  // the BIDDING chip (bidding only)
  // Landscape from 640x360 up (no word may go there): the side column
  // even if the cards step down. Smaller screens drop words instead.
  { id: 'side2', side: 'status', land: true, big: true },
  { id: 'b2', side: 'status', small: true },  // the TRUMP word
  { id: 'a3', side: 'score', small: true },   // the TRICKS word
  { id: 'b1', side: 'status', small: true },  // HAND n
  { id: 'a4', side: 'score', small: true },   // the big number at 24 px, small marks
  { id: 'c1', side: 'status', small: true },  // the turn chip's padding and gap (no words go)
];

const BURGER = (
  <svg width="20" height="16" viewBox="0 0 20 16" shape-rendering="crispEdges" aria-hidden="true">
    <path d="M0 0h20v4h-20zM0 6h20v4h-20zM0 12h20v4h-20z" fill="currentColor" />
  </svg>
);

/**
 * The bid buttons a round usually shows, for the room the controls row
 * keeps between your turns: Order up, Pass and Go alone in round one;
 * a Call and a Go alone for each suit but the turned-down one, and Pass,
 * in round two.
 *
 * @param {string} cs
 * @param {string|null|undefined} upCard
 */
function reserveBidButtons(cs, upCard) {
  const up = upCard && upCard.length === 2 ? upCard[1] : null;
  if (cs === 'bid_r1') return [{ role: 'order_up', suit: up }, { role: 'pass', suit: null }, { role: 'go_alone', suit: up }];
  const suits = ['S', 'H', 'D', 'C'].filter((x) => x !== up);
  return [...suits.map((x) => ({ role: 'call', suit: x })), { role: 'pass', suit: null }, ...suits.map((x) => ({ role: 'go_alone', suit: x }))];
}

/**
 * @param {{
 *   service: any,
 *   mySeatIndex: 0|1|2|3|null,
 *   roomOccupants?: Array<{display_name?: string, connection_status?: string, seat_index?: number|null}>,
 *   onLeave?: () => void,
 *   serviceError?: { code: string, message: string }|null,
 * }} props
 */
export function NetworkedGameView({ service, mySeatIndex: mySeatIndexProp, roomOccupants = [], onLeave, serviceError = null }) {
  const { view, sendGameCommand, dismissError, mySeatIndex: resolvedSeatIndex, sending, readyState, markHandReady, clearReady } =
    useGameWs({ service, mySeatIndex: mySeatIndexProp, roomOccupants });
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Item 11: one leave per tap sequence, focus into the confirm, Escape
  // means Stay, and the confirm closes if the game ends under it.
  const leaveOnce = useMemo(() => (onLeave ? onceOnly(onLeave) : null), [onLeave]);
  /** @type {{ current: HTMLDivElement|null }} */
  const leaveConfirmRef = useRef(null);
  /** @type {{ current: HTMLButtonElement|null }} */
  const menuButtonRef = useRef(null);
  const stayInGame = useCallback(() => {
    setConfirmLeave(false);
    menuButtonRef.current?.focus();
  }, []);
  // A layout effect: focus and the Escape key must work the moment the
  // confirm shows, not one frame later.
  useLayoutEffect(() => {
    if (!confirmLeave) return undefined;
    const stay = leaveConfirmRef.current?.querySelector('button[data-action="leave-stay"]');
    if (stay instanceof HTMLElement) stay.focus();
    const onKey = (/** @type {KeyboardEvent} */ e) => {
      if (leaveConfirmKeyAction(e.key) !== 'stay') return;
      e.preventDefault();
      stayInGame();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmLeave, stayInGame]);
  useEffect(() => {
    if (view.gameOver) setConfirmLeave(false);
  }, [view.gameOver]);
  // Use the hook's resolved seat (GAME_INITIALIZED.your_seat_index when
  // available, room-layer prop otherwise) for every downstream check.
  const mySeatIndex = resolvedSeatIndex;
  const viewerSeat = (mySeatIndex ?? 0) + 1;
  const myLuaSeat = (mySeatIndex ?? -1) + 1;
  const myTeam = teamOfSeatLua(viewerSeat);
  const { clockOffsetMs } = useClockOffset({ service });
  /** @type {{ current: HTMLCanvasElement | null }} */
  const canvasRef = useRef(null);
  /** @type {{ current: HTMLDivElement | null }} */
  const stageRef = useRef(null);
  // devicePixelRatio, re-read on zoom or a move to another screen.
  const [dpr, setDpr] = useState(() => readDevicePixelRatio());
  useEffect(() => watchDevicePixelRatio(setDpr), []);
  // Restyle step 2 (drawing): the stage (the canvas and its hit targets)
  // starts on a whole device pixel, whatever moves it.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    return keepStageAligned(stage, dpr);
  }, [dpr]);
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setViewport({ w: window.innerWidth, h: window.innerHeight }));
    };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, []);
  const [settings, updateSettings] = useSettings();
  const osReducedMotion = usePrefersReducedMotion();
  // C13: the system setting or the in-app switch.
  const reducedMotion = osReducedMotion || !!settings.reduced_motion_override;

  const [hoveredCardId, setHoveredCardId] = useState(/** @type {string|null} */ (null));
  const [hoverLift, setHoverLift] = useState(0);
  // M4-13: dealer-discard selection state.
  const [discardSelection, setDiscardSelection] = useState(/** @type {string|null} */ (null));
  // T6, transient illegal-tap feedback; nonce restarts the shake.
  const [illegalTap, setIllegalTap] = useState(
    /** @type {{card_id:string, message:string, nonce:number, refocus?:boolean}|null} */ (null),
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  // Review blocker fix: the raise gate must NOT key off hoveredCardId,
  // a touch tap synthesizes mouseenter→click.
  const [raisedCardId, setRaisedCardId] = useState(/** @type {string|null} */ (null));
  // A device can switch between touch and a mouse: follow the media query.
  const [coarsePointer, setCoarsePointer] = useState(() => typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(pointer: coarse)');
    const on = () => setCoarsePointer(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  // T9, up-card spotlight on a fresh bid_r1.
  const upcardIntroRef = useRef(/** @type {{key: string|null, raf: number|null}} */ ({ key: null, raf: null }));
  const [upcardIntroT, setUpcardIntroT] = useState(1);
  useEffect(() => {
    if (!illegalTap) return undefined;
    const t = setTimeout(() => setIllegalTap(null), 1100);
    return () => clearTimeout(t);
  }, [illegalTap]);
  // F7: put keyboard focus back on the remounted card button.
  useEffect(() => {
    if (!illegalTap?.refocus || typeof document === 'undefined') return;
    const btn = document.querySelector(`button.hit-zone[data-card-id="${illegalTap.card_id}"]`);
    if (btn instanceof HTMLElement) btn.focus();
  }, [illegalTap?.nonce, illegalTap?.refocus, illegalTap?.card_id]);
  const cardSnapRef = useRef(/** @type {ReturnType<typeof createCardSnapDetector>|null} */ (null));
  if (cardSnapRef.current === null) cardSnapRef.current = createCardSnapDetector();
  const trickResRef = useRef(/** @type {ReturnType<typeof createTrickResolutionDetector>|null} */ (null));
  if (trickResRef.current === null) trickResRef.current = createTrickResolutionDetector();
  const slideOverlay = useCardPlayAnimation({ snapshot: view.gameState, reducedMotion });
  // #bug4, trick-completion sequence (slide in, hold, sweep to the winner).
  const trickSeqFrame = useTrickSequence({ snapshot: view.gameState, reducedMotion });
  const trumpDecidedRef = useRef(/** @type {ReturnType<typeof createTrumpDecidedDetector>|null} */ (null));
  if (trumpDecidedRef.current === null) trumpDecidedRef.current = createTrumpDecidedDetector();
  const [trumpEvent, setTrumpEvent] = useState(/** @type {any} */ (null));
  // Restyle step 2 (pace): from the moment a trick's 4th card shows until
  // its clear ends, the hand is locked, as in the mockup (G.locked): taps
  // and keys play nothing and no card looks playable. It follows the
  // sequence the table runs (reduced motion's short hold too), so it ends
  // the frame the trick clears.
  const trickClearing = trickSeqFrame !== null;
  // No card is ever drawn under the trump announcement: a card played
  // while it shows (a human may lead at once) closes it in the same
  // render that draws the card.
  const trickHasCards = trickClearing
    || Object.keys(view.gameState?.trick_plays ?? {}).length > 0;
  useEffect(() => {
    if (trickHasCards && trumpEvent) setTrumpEvent(null);
  }, [trickHasCards, trumpEvent]);

  // C3 (2026-06-11), connection status: never claim "Your turn" over a
  // dead socket.
  const [connStatus, setConnStatus] = useState(() =>
    (service && typeof service.getStatus === 'function') ? service.getStatus() : 'open');
  const [, setReconnectTick] = useState(0);
  useEffect(() => {
    if (!service || typeof service.subscribeStatus !== 'function') return undefined;
    return service.subscribeStatus((status) => { setConnStatus(status); setReconnectTick((n) => n + 1); });
  }, [service]);
  const connectionLost = connStatus !== 'open';
  const connectionPhase = typeof service?.getConnectionPhase === 'function' ? service.getConnectionPhase() : null;
  const connectErrorText = connectErrorBannerText(serviceError);
  // Item 1 (round 3): a sent move locks every move control until its answer.
  const movesLocked = connectionLost || sending;
  const lockText = moveLockText({ connectionLost, sending });
  useEffect(() => {
    if (!connectionLost) return undefined;
    const timer = setInterval(() => setReconnectTick((n) => n + 1), 1_000);
    return () => clearInterval(timer);
  }, [connectionLost]);

  const [loadTimedOut, setLoadTimedOut] = useState(false);
  useEffect(() => {
    if (view.gameState) {
      setLoadTimedOut(false);
      return undefined;
    }
    const timer = setTimeout(() => setLoadTimedOut(true), 10_000);
    return () => clearTimeout(timer);
  }, [view.gameState]);

  // C4, rejection toasts auto-dismiss after 4s.
  useEffect(() => {
    if (!view.errorToast) return undefined;
    const timer = setTimeout(() => dismissError(), 4_000);
    return () => clearTimeout(timer);
  }, [view.errorToast, dismissError]);

  // ---- The model: names, tags and the layout ----
  const rawSnap = view.gameState;
  const snap = useMemo(() => (rawSnap ? normalizeSnapshotShape(rawSnap) : null), [rawSnap]);
  const seatLabels = useMemo(() => seatLabelsFromSnapshot(rawSnap, mySeatIndex), [rawSnap, mySeatIndex]);
  const plainLabels = useMemo(() => plainSeatLabelsFromSnapshot(rawSnap, mySeatIndex), [rawSnap, mySeatIndex]);
  const tags = useMemo(() => tagContents(snap, viewerSeat, seatLabels), [snap, viewerSeat, seatLabels]);
  const tagsKey = JSON.stringify(tags);
  const cs = view.currentState;
  const isBidding = cs === 'bid_r1' || cs === 'bid_r2';
  // C9: the bid buttons (and the dealer line) the controls row must hold.
  // Between your turns it keeps room for this round's usual buttons, so
  // the table does not move when your turn comes.
  const myBidButtons = isBidding && view.isMyTurn && snap ? orderedBidButtons(snap, viewerSeat) : [];
  const bidSpec = isBidding && snap ? {
    buttons: myBidButtons.length
      ? myBidButtons.map((b) => ({ role: b.role, suit: b.suit || null }))
      : reserveBidButtons(cs, snap.up_card),
    line: myBidButtons.length ? bidLineText(snap, viewerSeat, plainLabels) : (dealerContextLine(snap, plainLabels, viewerSeat) ?? ''),
  } : null;
  const bidKey = bidSpec ? JSON.stringify(bidSpec) : '';
  // The top bar's fit steps (FIT_STEPS); 'side' also moves the status
  // row into the board's right-hand column, so the layout needs it.
  const [barFit, setBarFit] = useState(/** @type {string[]} */ ([]));
  const sideStatus = barFit.includes('side2') ? 'force' : barFit.includes('side');
  // System-font mode sizes the seat tags in the system face (K3); the
  // pixel faces keep the layout's own advance tables.
  const systemFont = !!settings.system_font;
  const measure = useMemo(() => (systemFont ? systemTextMeasure() : undefined), [systemFont]);
  const L = useMemo(
    () => creamLayout(viewport.w, viewport.h, JSON.parse(tagsKey), { bid: bidKey ? JSON.parse(bidKey) : null, sideStatus, measure }),
    [viewport.w, viewport.h, tagsKey, bidKey, sideStatus, measure],
  );
  const bySlot = seatsBySlot(viewerSeat);

  // Hover lift (C13): a legal card rises 6 px over 70 ms on a fine
  // pointer; nothing moves under reduced motion.
  useEffect(() => {
    if (!hoveredCardId || reducedMotion || coarsePointer) { setHoverLift(0); return undefined; }
    const start = performance.now();
    let raf = 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / 70);
      setHoverLift(Math.round(HOVER_LIFT * t));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [hoveredCardId, reducedMotion, coarsePointer]);

  const isTrickPlay = cs === 'trick_play';
  const isDealerDiscard = cs === 'dealer_discard';
  const isMyDealerDiscard = isDealerDiscard && view.gameState?.dealer_seat === myLuaSeat;
  const isHandOver = cs === 'end_of_hand' || cs === 'score';

  // The trick winner, marked from the moment the last card lands (C7).
  const doneTricks = asList(snap?.completed_tricks);
  const lastDone = doneTricks[doneTricks.length - 1] ?? null;
  const winnerSeat = trickSeqFrame && !trickSeqFrame.slide && lastDone ? Number(lastDone.winner_seat) : null;
  // J5: from the 4th card until the clear ends, the turn chip and the
  // screen reader's status name who takes the trick.
  const trickTaker = trickClearing && lastDone ? Number(lastDone.winner_seat) : null;
  // Where the trick sequence is: the 4th card's slide, the hold, the
  // sweep, or nothing.
  const trickPhase = !trickSeqFrame ? '' : trickSeqFrame.slide ? 'slide' : trickSeqFrame.trick_resolution ? 'sweep' : 'hold';
  // "+1 TRICK" in the winning team's score block, as in the mockup
  // (shared/app.js sweep, plusTrick): it shows from the sweep's start
  // for 700 ms, with its own squash, and the block squashes too
  // (cream-table.css cream-plus-pop, cream-score-pop). Under reduced
  // motion there is no sweep: it shows when the trick clears, still.
  const doneKey = `${snap?.hand_index ?? ''}:${doneTricks.length}`;
  const doneRef = useRef(/** @type {string|null} */ (null));
  const popDueRef = useRef(/** @type {{team:'US'|'THEM', key:string}|null} */ (null));
  const popTimerRef = useRef(/** @type {ReturnType<typeof setTimeout>|undefined} */ (undefined));
  const [pop, setPop] = useState(/** @type {{team:'US'|'THEM', key:string}|null} */ (null));
  useLayoutEffect(() => {
    const prev = doneRef.current;
    doneRef.current = doneKey;
    if (prev === null || prev === doneKey || !lastDone || doneTricks.length === 0) return;
    popDueRef.current = { team: teamOfSeatLua(Number(lastDone.winner_seat)) === myTeam ? 'US' : 'THEM', key: doneKey };
  }, [doneKey]);
  // A sweep, or a clear without one (reduced motion, a cut-short trick).
  const popNow = trickPhase === 'sweep' || trickPhase === '';
  useLayoutEffect(() => {
    const due = popDueRef.current;
    if (!due || !popNow) return;
    popDueRef.current = null;
    setPop(due);
    clearTimeout(popTimerRef.current);
    popTimerRef.current = setTimeout(() => setPop((p) => (p === due ? null : p)), PLUS_TRICK_MS);
  }, [popNow, doneKey]);
  useEffect(() => () => clearTimeout(popTimerRef.current), []);

  // Up-card in the dealer's cell (C9).
  const dealerSlot = snap?.dealer_seat ? slotOfSeat(snap.dealer_seat, viewerSeat) : null;
  const upcard = snap?.up_card && dealerSlot
    ? (cs === 'bid_r1'
      ? { card_id: snap.up_card, slot: dealerSlot, mode: /** @type {const} */ ('bid'), intro: upcardIntroT }
      : cs === 'bid_r2'
        ? { card_id: snap.up_card, slot: dealerSlot, mode: /** @type {const} */ ('turned') }
        : isDealerDiscard
          ? { card_id: snap.up_card, slot: dealerSlot, mode: /** @type {const} */ ('pickup'), selected: discardSelection === snap.up_card }
          : null)
    : null;

  const tagCenters = useMemo(() => Object.fromEntries(SLOTS.map((s) => {
    const r = L.tags[s];
    return [s, { x: r.x + r.w / 2, y: r.y + r.h / 2 }];
  })), [L]);

  // Canvas render, driven by the server snapshot. A layout effect, so
  // the canvas and the DOM cell names show the same frame (a plain
  // effect runs after paint: for one frame a name sat over a new card).
  useLayoutEffect(() => {
    if (!canvasRef.current) return;
    const ctx = prepareHiDpiCanvas(canvasRef.current, L.board.w, L.board.h, dpr);
    if (!ctx) return;
    if (!snap) return;
    const lift = hoveredCardId && !trickClearing ? { card_id: hoveredCardId, lift: hoverLift } : null;
    const drawn = renderCreamBoard(ctx, L, {
      viewerSeat,
      snap,
      frame: trickSeqFrame,
      liveSlide: slideOverlay,
      winnerSeat,
      hover: lift,
      // C14: on your trick turn the cards you cannot play are greyed.
      legalPlays: isTrickPlay && view.isMyTurn && !trickClearing && view.legalPlays.length ? view.legalPlays : null,
      selectedCardId: discardSelection ?? raisedCardId,
      upcard,
      tagCenters,
    });
    // A card that carries the WIN mark is not a plain resting card.
    const trick = drawn.trick.map((d) => (d.winner ? { ...d, resting: false } : d));
    canvasRef.current.dataset.drawnCards = JSON.stringify({ hand: drawn.hand, trick, upcard: drawn.upcard });
    if (snap.trick_plays !== undefined) cardSnapRef.current?.detect({ trick_plays: snap.trick_plays });
    // M5 #10, trick-resolution audio + chip burst over the winner's tag.
    const resolutions = trickResRef.current?.detect(snap) || [];
    for (const r of resolutions) {
      const wonEv = trickWonEventFor(r.winner_seat, myTeam);
      audio.emit(wonEv, { winner_seat: r.winner_seat, winning_card: r.winning_card });
      if (r.is_bower) audio.emit('bower_played', { card_id: r.winning_card, bower: r.is_bower });
      const stage = stageRef.current?.getBoundingClientRect();
      const ox = stage ? stage.left : L.board.x;
      const oy = stage ? stage.top : L.board.y;
      if (r.is_loner_sweep) {
        audio.emit(EVENTS.LONER_SWEEP, { winner_seat: r.winner_seat });
        if (!reducedMotion) {
          getParticleLayer().burst('FIRE', { x: ox + L.grid.x + L.grid.w / 2, y: oy + L.grid.y + L.grid.h / 2 }, { count: 10, durationMs: 1200 });
        }
      } else if (!reducedMotion) {
        const teamColor = wonEv === EVENTS.TRICK_WON_YOURS ? '#24548f' : '#a3352a';
        const c = tagCenters[slotOfSeat(r.winner_seat, viewerSeat) ?? 'S'];
        const landMs = trickSeqInternals.PLAY_IN_MS + trickSeqInternals.HOLD_MS + trickSeqInternals.SWEEP_MS;
        setTimeout(() => {
          getParticleLayer().burst('BURST', { x: ox + c.x, y: oy + c.y }, { count: 5, color: teamColor });
        }, landMs);
      }
    }
    // C5, the trump announcement when bidding concludes.
    const trumpFired = trumpDecidedRef.current?.detect(snap);
    if (trumpFired) setTrumpEvent(trumpFired);
  }, [snap, L, dpr, hoveredCardId, hoverLift, raisedCardId, discardSelection, slideOverlay, trickSeqFrame, trickClearing, upcardIntroT, winnerSeat, tagCenters, isTrickPlay, view.isMyTurn, view.legalPlays]);

  // T9 driver: a fresh deal runs the up-card's 1 s intro.
  useEffect(() => {
    const s = view.gameState;
    if (!s || view.currentState !== 'bid_r1' || !s.up_card) return undefined;
    const hist = s.bid_history;
    const histLen = Array.isArray(hist) ? hist.length : Object.keys(hist || {}).length;
    if (histLen > 0) return undefined;
    const key = `${s.hand_index}:${s.up_card}`;
    const ref = upcardIntroRef.current;
    if (ref.key === key) return undefined;
    ref.key = key;
    if (reducedMotion) return undefined;
    if (ref.raf != null) cancelAnimationFrame(ref.raf);
    const start = performance.now();
    const DUR = 1000;
    const step = (ts) => {
      const t = Math.min(1, (ts - start) / DUR);
      setUpcardIntroT(t);
      ref.raf = t < 1 ? requestAnimationFrame(step) : null;
    };
    setUpcardIntroT(0);
    ref.raf = requestAnimationFrame(step);
    return undefined;
  }, [view.gameState, view.currentState, reducedMotion]);
  useEffect(() => () => {
    const ref = upcardIntroRef.current;
    if (ref.raf != null) cancelAnimationFrame(ref.raf);
    ref.raf = null;
  }, []);

  // M4-12 bid handler.
  const handleBid = useCallback((bid) => {
    if (!sendGameCommand || movesLocked) return;
    sendGameCommand({ type: 'BID', payload: bid });
  }, [sendGameCommand, movesLocked]);

  // M4-13 card click: the dealer's discard pick, or a play.
  const handleCardClick = useCallback((hit) => {
    if (!sendGameCommand || movesLocked || trickClearing) return;
    const s = view.gameState;
    if (view.currentState === 'dealer_discard') {
      if (s?.dealer_seat === myLuaSeat) setDiscardSelection(hit.card_id);
      return;
    }
    if (view.currentState === 'trick_play' && view.isMyTurn) {
      const isLegal = view.legalPlays.length > 0 ? view.legalPlays.includes(hit.card_id) : hit.legal;
      if (!isLegal) {
        audio.emit('illegal_play', { card_id: hit.card_id });
        const hadFocus = typeof document !== 'undefined'
          && document.activeElement?.getAttribute?.('data-card-id') === hit.card_id;
        setIllegalTap({ card_id: hit.card_id, message: illegalPlayMessage(s), nonce: Date.now(), refocus: hadFocus });
        return;
      }
      // T15, tap-to-raise on coarse pointers: the first tap lifts.
      const selection = { raisedCardId, hoveredCardId, discardSelection };
      const outcome = cardTapOutcome(selection, hit.card_id, !!coarsePointer);
      applySelection(selection, outcome.selection, { setRaisedCardId, setHoveredCardId, setDiscardSelection });
      if (outcome.play) sendGameCommand({ type: 'PLAY_CARD', payload: { card_id: outcome.play } });
    }
  }, [sendGameCommand, movesLocked, trickClearing, view.currentState, view.isMyTurn, view.legalPlays, view.gameState, myLuaSeat, coarsePointer, raisedCardId, hoveredCardId, discardSelection]);

  const handleConfirmDiscard = useCallback(() => {
    if (!sendGameCommand || movesLocked || !discardSelection) return;
    const sent = sendGameCommand({ type: 'DEALER_DISCARD', payload: { card_id: discardSelection } });
    if (sent) setDiscardSelection(null);
  }, [sendGameCommand, movesLocked, discardSelection]);

  // MARK_HAND_READY, the post-hand "Continue".
  const handleHandReady = useCallback(() => {
    if (!markHandReady || movesLocked || readyState !== 'idle') return;
    markHandReady();
  }, [markHandReady, movesLocked, readyState]);

  const recapSummary = isHandOver
    ? handSummaryFromSnapshot(view.gameState, view.handScoredEvent, { requireScoredEvent: true })
    : null;
  const recapOpen = !!recapSummary && canShowRecap(view.handScoredEvent, trickSeqFrame, view.recapSkipsWait);
  // B3b U5: clear a stale lift or pick when it stops being usable.
  useEffect(() => {
    selectionSyncEffect(
      { raisedCardId, hoveredCardId, discardSelection },
      { currentState: view.currentState, isMyTurn: view.isMyTurn, isMyDiscardTurn: isMyDealerDiscard, connectionOpen: !connectionLost },
      { setRaisedCardId, setHoveredCardId, setDiscardSelection },
    );
  }, [view.currentState, view.isMyTurn, isMyDealerDiscard, connectionLost, raisedCardId, hoveredCardId, discardSelection]);
  const handIndex = view.gameState?.hand_index ?? null;
  useEffect(() => {
    if (!isHandOver && !view.passedOut) clearReady();
  }, [isHandOver, view.passedOut, handIndex, clearReady]);
  const waitingText = waitingForText(view.gameState?.seat_occupants, myLuaSeat, roomOccupants);
  const readyButton = readyButtonState({ readyState, connectionLost, sending, waitingText });

  // B2-14: the screen-reader status follows current_turn_seat exactly.
  const statusText = useMemo(() => networkedStatusText({
    connectionLost, gameOver: view.gameOver, gameState: view.gameState, myLuaSeat,
    currentState: view.currentState, isMyDealerDiscard, trickTaker,
  }), [connectionLost, view.gameOver, view.gameState, view.currentState, isMyDealerDiscard, myLuaSeat, trickTaker]);
  const sitOutNote = isMyDealerDiscard ? null : sitOutNoteText(view.gameState, myLuaSeat > 0 ? myLuaSeat : null, plainLabels);
  const viewerTeam = mySeatIndex != null ? teamOfSeatLua(myLuaSeat) : null;
  const finalWinner = view.gameOver
    ? deriveWinner({ winningTeam: null, summary: null, scores: view.finalScores })
    : null;

  // C8: the turn's deadline. The turn chip's seconds (TurnSeconds) and the
  // active tag's bar (TurnBar) count it down themselves; the view itself
  // re-renders only when the seconds gain or lose a digit (the top bar is
  // fitted to that), not every frame.
  const turnDeadline = view.bidTimerDeadlineMs ?? view.playTimerDeadlineMs ?? null;
  const timerDigits = useTurnDigits(turnDeadline, clockOffsetMs);
  const turnSeat = snap?.current_turn_seat ?? null;
  const activeSlot = turnSeat != null && (isBidding || isTrickPlay || isDealerDiscard) ? slotOfSeat(turnSeat, viewerSeat) : null;

  // C10: the menu is a dialog. Focus moves into it when it opens; Escape
  // or a click outside closes it and puts focus back on the menu button;
  // Tab stays inside while it is open. A pick closes it too.
  const menuRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const closeMenu = useCallback(() => { setMenuOpen(false); menuButtonRef.current?.focus(); }, []);
  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    const first = menuRef.current?.querySelector('button');
    if (first instanceof HTMLElement) first.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); closeMenu(); return; }
      if (e.key !== 'Tab' || !menuRef.current) return;
      const items = [...menuRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el instanceof HTMLElement && !el.hasAttribute('disabled') && el.getClientRects().length > 0);
      if (!items.length) return;
      const at = items.indexOf(/** @type {any} */ (document.activeElement));
      const next = e.shiftKey ? (at <= 0 ? items.length - 1 : at - 1) : (at === -1 || at === items.length - 1 ? 0 : at + 1);
      e.preventDefault();
      /** @type {HTMLElement} */ (items[next]).focus();
    };
    // A click outside closes the menu and does nothing else (the menu is
    // modal): its press neither moves focus nor clicks what is under it.
    const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDown = (e) => {
      const t = /** @type {Node} */ (e.target);
      if (menuRef.current?.contains(t) || menuButtonRef.current?.contains(t)) return;
      e.preventDefault();
      document.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', swallow, true), 1_000);
      closeMenu();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown, true); };
  }, [menuOpen, closeMenu]);
  // The log sheet: Escape closes it (when nothing opened over it takes the
  // key) and puts focus back on the menu button, as Close log does.
  const closeLog = useCallback(() => { setLogOpen(false); menuButtonRef.current?.focus(); }, []);
  useLayoutEffect(() => {
    if (!logOpen || menuOpen || settingsOpen || confirmLeave) return undefined;
    const onKey = (/** @type {KeyboardEvent} */ e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (e.target instanceof Element && e.target.closest('dialog')) return;
      e.preventDefault();
      closeLog();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [logOpen, menuOpen, settingsOpen, confirmLeave, closeLog]);
  const howTo = useContext(OverlayNavContext);

  // ---- Words ----
  const score = scoreModel(snap, viewerSeat);
  const chip = turnChipText(snap, viewerSeat, plainLabels, trickTaker);
  const trump = snap?.trump_suit ?? null;
  // Under 360x640 (phones) and 640x360 (landscape) the owner allows the
  // TRUMP word, the TRICKS word and HAND n to go, in that order.
  const smallScreen = (L.mode === 'phone' && (L.vw < 360 || L.vh < 640)) || (L.mode === 'land' && (L.vw < 640 || L.vh < 360));

  // The top bar drops words, one step at a time, until its boxes fit
  // (cream-table.css fit-*). Measured before paint, only when its inputs
  // (fitKey) or its steps change; it starts again when the window or the
  // words change.
  // The bar is measured in its pixel fonts: a fallback face is wider.
  // J1: document.fonts.status can read 'loaded' before the table's text
  // has even asked for Tiny5 and Silkscreen, so the view loads those
  // faces itself and waits for them. Until then the bar is hidden (its
  // words would not show anyway: the faces use font-display: block) and
  // not fitted; the fit starts again once they are in.
  const [fontsReady, setFontsReady] = useState(pixelFontsLoaded);
  useEffect(() => {
    if (fontsReady) return undefined;
    let live = true;
    loadPixelFonts().then(() => { if (live) setFontsReady(true); });
    return () => { live = false; };
  }, [fontsReady]);
  // Any later font load (a face that came in after the 3 s wait) fits
  // the bar again.
  const [fontLoads, setFontLoads] = useState(0);
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return undefined;
    const onLoad = () => setFontLoads((n) => n + 1);
    document.fonts.addEventListener('loadingdone', onLoad);
    return () => document.fonts.removeEventListener('loadingdone', onLoad);
  }, []);
  // The system-font switch reaches the page as body.system-font (set by
  // useSettings after the render that reads the setting), so the bar is
  // fitted again when that class comes or goes.
  const [systemFontOn, setSystemFontOn] = useState(() => typeof document !== 'undefined' && document.body.classList.contains('system-font'));
  useEffect(() => {
    const body = document.body;
    const update = () => setSystemFontOn(body.classList.contains('system-font'));
    const watch = new MutationObserver(update);
    watch.observe(body, { attributes: true, attributeFilter: ['class'] });
    update();
    return () => watch.disconnect();
  }, []);
  const headerRef = useRef(/** @type {HTMLElement|null} */ (null));
  // Everything the bar shows that can change its size: the fonts (and the
  // system-font switch), the window, HAND n, the chips' words, the scores,
  // the seconds (shown or not, and how many digits) and a toast.
  const showSeconds = turnDeadline != null && !!activeSlot && !trickClearing;
  const fitKey = `${fontsReady ? 1 : 0}.${fontLoads}.${systemFontOn ? 1 : 0}|${L.vw}x${L.vh}|${L.mode}|${handNumber(snap)}|${chip}|${trump}|${score.US.score}|${score.THEM.score}|${score.target}|${showSeconds ? timerDigits : 0}|${view.errorToast ? 1 : 0}`;
  const fitKeyRef = useRef('');
  const fitWarnRef = useRef('');
  useLayoutEffect(() => {
    if (fitKeyRef.current !== fitKey) {
      fitKeyRef.current = fitKey;
      if (barFit.length) { setBarFit([]); return; }
    }
    const el = headerRef.current;
    if (!el || !fontsReady) return;
    // Mid-resize the bar is still laid out for the old window; measure
    // once the layout has caught up (the resize sets it a frame later).
    if (L.vw !== Math.max(1, Math.floor(window.innerWidth)) || L.vh !== Math.max(1, Math.floor(window.innerHeight))) return;
    const c = topBarCrowding(el);
    if (!c.scores && !c.status) return;
    // A crowded score side (phones: the blocks against the menu) skips
    // the status row's steps. Past the last allowed step the bar stays
    // as it is, and the console names it.
    const next = FIT_STEPS.find((st) => !barFit.includes(st.id) && (c.status || st.side === 'score')
      && (!st.small || smallScreen) && (!st.big || !smallScreen) && (!st.land || L.mode === 'land'));
    if (next) setBarFit([...barFit, next.id]);
    else if (fitWarnRef.current !== fitKey) {
      fitWarnRef.current = fitKey;
      console.error(`[cream-table] the top bar does not fit its words at ${L.vw}x${L.vh} (steps: ${barFit.join(' ')}): ${c.why.join('; ')}`);
    }
  }, [fitKey, barFit]);
  // The turn chip's name, shortened with a dot (the tags' rule) once the
  // two-line status row still cannot hold it whole.
  const shortChip = barFit.includes('name')
    ? turnChipText(snap, viewerSeat, Object.fromEntries(Object.entries(plainLabels || {}).map(([k, v]) => [k, shortName(String(v))])), trickTaker)
    : chip;
  const last = lastTrick(snap);
  const showLastTrick = isTrickPlay;
  // The hint line; "Sending…" shows here while a move is out (the turn
  // chip keeps its words and seconds).
  /** @type {{text:string, testid:string, msg?:boolean, sending?:boolean}|null} */
  let hint = null;
  if (illegalTap) hint = { text: illegalTap.message.toUpperCase(), testid: 'illegal-play-msg', msg: true };
  else if (lockText && isTrickPlay && view.isMyTurn) hint = { text: lockText, testid: 'hand-lock-reason', msg: true, sending };
  else if (sending) hint = { text: SENDING_TEXT, testid: 'hint-line', msg: true, sending: true };
  else if (sitOutNote) hint = { text: sitOutNote, testid: 'sit-out-note' };
  // While a trick clears nobody is on turn yet (the mockup's line is empty).
  else if (snap) hint = { text: trickClearing ? '' : hintText(snap, viewerSeat, plainLabels), testid: 'hint-line' };

  const hand = (snap?.hands?.[viewerSeat] ?? []);
  const handRects = snap && snap.sitting_out_seat !== viewerSeat ? handCardRects(L, hand.length) : [];
  const legalSet = isTrickPlay && view.isMyTurn && !trickClearing && view.legalPlays.length ? new Set(view.legalPlays) : null;
  const passed = passedSeats(snap);
  const playsOnTable = trickSeqFrame ? trickSeqFrame.trick_plays : (snap?.trick_plays ?? []);
  const filled = new Set(asList(playsOnTable).map((p) => slotOfSeat(Number(p.seat), viewerSeat)));
  if (upcard) filled.add(upcard.slot);
  const winCard = winnerSeat != null && trickSeqFrame && !trickSeqFrame.trick_resolution
    ? asList(trickSeqFrame.trick_plays).find((p) => Number(p.seat) === winnerSeat) ?? null
    : null;
  const bidOn = isBidding && view.isMyTurn && myBidButtons.length > 0 && !!L.bid;
  const scoreTeams = /** @type {const} */ (['US', 'THEM']);
  const fitClasses = barFit.map((step) => ` fit-${step}`).join('');
  const sweeping = !!trickSeqFrame;

  // C5: each empty cell's seat name starts on a whole CSS pixel. Flex
  // centring lands a name half a pixel off whenever the cell and the
  // name differ in odd/even, so measure each name once it changes (its
  // text, its cell, or the fonts) and shift it by at most half a pixel
  // on CSS translate: the words move without touching the flex layout,
  // the text or the cell boxes. Font loads and the system-font switch
  // re-snap through their own listeners; nothing reads layout per frame.
  const cellNamesKey = `${tagsKey}|${SLOTS.map((s) => `${L.cells[s].w}x${L.cells[s].h}:${passed.has(bySlot[s]) ? 1 : 0}:${filled.has(s) ? 1 : 0}`).join(',')}|${fontsReady ? 1 : 0}.${fontLoads}`;
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof document === 'undefined') return undefined;
    alignCellNames(stage);
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; alignCellNames(stage); });
    };
    const doc = document;
    if (doc.fonts) doc.fonts.addEventListener('loadingdone', schedule);
    window.addEventListener('resize', schedule);
    const classes = new MutationObserver(schedule);
    if (doc.body) classes.observe(doc.body, { attributes: true, attributeFilter: ['class'] });
    return () => {
      cancelAnimationFrame(raf);
      raf = 0;
      if (doc.fonts) doc.fonts.removeEventListener('loadingdone', schedule);
      window.removeEventListener('resize', schedule);
      classes.disconnect();
    };
  }, [cellNamesKey]);

  // The game-over card on landscape tables keeps off the tags and the top
  // bar: it stacks into the felt right of the trick block (the hand is
  // empty by then).
  const blockRight = Math.max(...SLOTS.flatMap((s) => [L.tags[s].x + L.tags[s].w, L.cells[s].x + L.cells[s].w]));
  const freeW = L.board.w - 8 - (blockRight + 8);
  const gameOverLayout = L.mode === 'land' ? {
    compact: true, canvasW: L.board.w, canvasH: L.board.h,
    cardMaxWidth: freeW >= 240 ? freeW : null,
    obstacles: [...SLOTS.map((s) => L.tags[s]), { x: -L.board.x, y: -L.board.y, w: L.vw, h: L.headH }, ...(L.status ? [L.status] : [])],
  } : null;

  // The log sheet: beside the board where the window has room for both
  // (desktop: read the log and see the table; the centred board moves
  // left while the log is open), else over the board's lower part.
  const sideRoom = L.vw - L.board.w - 24;
  const logBeside = L.mode === 'desk' && sideRoom >= 200;
  const logW = Math.min(360, sideRoom);
  const boardX = logOpen && logBeside ? Math.max(8, Math.min(L.board.x, L.vw - 8 - logW - 8 - L.board.w)) : L.board.x;
  const logBox = logBeside ? {
    left: boardX + L.board.w + 8,
    width: logW,
    top: L.board.y,
    maxHeight: L.board.h,
  } : {
    left: L.board.x + 8,
    width: L.board.w - 16,
    bottom: L.vh - (L.board.y + L.board.h) + 8,
    maxHeight: Math.max(160, Math.round(L.board.h * 0.7)),
  };

  // C5: HAND n, the trump chip and the turn chip (in the top bar, or on
  // landscape beside the board when the bar has no room).
  const statusRow = (
        <div
          class={L.status ? 'cream-status side' : 'cream-status'}
          data-testid="status-row"
          style={L.status ? { left: L.status.x, top: L.status.y, width: L.status.w, height: L.status.h } : undefined}
        >
          <span class="table-title" data-testid="hand-number">HAND {handNumber(snap)}</span>
          <span class={trump ? 'trump-chip' : 'trump-chip no-trump'} data-testid="trump-chip">
            <span class={trump ? 'trump-label' : 'bid-label'}>{trump ? 'TRUMP' : 'BIDDING'}</span>
            {trump && (
              <span class={`trump-word suit-${trump}`}>
                <SuitIcon suit={trump} px={2} />
                <span class="trump-word-text">{SUIT_WORD[trump]}</span>
              </span>
            )}
          </span>
          {/* Two-line status row (fit-two): the turn chip takes line two. */}
          <span class="status-break" aria-hidden="true" />
          {chip ? (
            <span class={`turn-chip${turnSeat === viewerSeat && chip.startsWith('YOUR') ? ' mine' : ''}`} data-testid="turn-chip">
              <span class="turn-text">{shortChip}</span>
              {/* No seconds while a trick clears (J5): the chip names the taker. */}
              {showSeconds && (
                <TurnSeconds deadlineMs={/** @type {number} */ (turnDeadline)} clockOffsetMs={clockOffsetMs} />
              )}
            </span>
          ) : null}
          <span class="sr-only" data-testid="networked-game-status" aria-live="polite">{statusText}</span>
          {view.errorToast && (
            <span
              class="toast"
              data-testid="networked-game-toast"
              data-error-code={view.errorToast.code}
              title={view.errorToast.message ?? view.errorToast.code}
              role="status"
              aria-live="assertive"
            >
              <span class="toast-text">{view.errorToast.message ?? view.errorToast.code}</span>
            </span>
          )}
        </div>
  );

  return (
    <section
      class={`networked-game-view cream-view cream-mode-${L.mode}${reducedMotion ? ' reduce' : ''}${fontsReady ? '' : ' fonts-wait'}${fitClasses}`}
      data-bar-fit={barFit.join(' ')}
      style={{ '--head-h': `${L.headH}px` }}
      data-testid="networked-game-view"
      data-cur-state={view.currentState ?? null}
      data-is-my-turn={view.isMyTurn ? 'true' : 'false'}
      data-my-seat-index={mySeatIndex ?? -1}
      data-mode={L.mode}
      data-portrait={L.mode === 'phone' ? 'true' : 'false'}
      data-compact={L.mode === 'land' ? 'true' : 'false'}
      // F5 e2e seam: whether the last trick's sweep is still moving.
      data-sweep-active={sweeping ? 'true' : 'false'}
    >
      <header class="cream-top" data-testid="networked-game-header" ref={headerRef} style={{ height: L.headH }}>
        <span class="brand" aria-hidden="true">EUCHRE.DEV</span>
        <div class="scores">
          {scoreTeams.map((t) => (
            <ScoreBlock
              key={t}
              label={t}
              score={score[t].score}
              target={score.target}
              tricks={score[t].tricks}
              mode={L.mode}
              pop={pop?.team === t}
              tight={barFit.includes('a0')}
              small={barFit.includes('a4')}
            />
          ))}
          <span class="to-target">TO {score.target}</span>
        </div>
        {L.status ? null : statusRow}
        <button
          type="button"
          class="btn menu-button"
          data-testid="menu-button"
          data-action="menu"
          ref={menuButtonRef}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen ? 'true' : 'false'}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {BURGER}
        </button>
      </header>

      {menuOpen && (
        <div
          class="table-menu cream"
          data-testid="table-menu"
          role="dialog"
          aria-modal="true"
          aria-labelledby="table-menu-title"
          ref={menuRef}
          style={{ top: L.headH + 4, maxHeight: L.vh - L.headH - 8 }}
        >
          <span class="menu-title" id="table-menu-title">MENU</span>
          <IdentityBadge />
          <button
            type="button"
            class="btn"
            data-testid="log-sheet-toggle"
            aria-expanded={logOpen ? 'true' : 'false'}
            onClick={() => { setMenuOpen(false); setLogOpen(true); }}
          >
            Game log
          </button>
          <button type="button" class="btn" data-action="open-settings" aria-label="Open settings" onClick={() => { setMenuOpen(false); setSettingsOpen(true); }}>
            Settings
          </button>
          {howTo && (
            <button type="button" class="btn" data-testid="how-to-play-trigger" aria-label="How to play" onClick={() => { setMenuOpen(false); howTo.open(); }}>
              How to play
            </button>
          )}
          {onLeave && (
            <button type="button" class="btn" data-testid="networked-game-leave" onClick={() => { setMenuOpen(false); setConfirmLeave(true); }}>
              Leave game
            </button>
          )}
        </div>
      )}

      <SettingsPanel
        open={settingsOpen}
        settings={settings}
        onUpdate={updateSettings}
        onClose={() => setSettingsOpen(false)}
      />
      {!view.gameState && loadTimedOut && (
        <ReconnectModal
          roomCode={(typeof service?.getRoomCode === 'function' && service.getRoomCode()) || ''}
          onRejoin={() => window.location.reload()}
          onLeave={() => { if (onLeave) onLeave(); }}
        />
      )}

      <div
        ref={stageRef}
        class="cream-stage"
        data-testid="table-stage"
        data-layout={JSON.stringify({
          mode: L.mode, shape: L.shape, fallback: L.fallback, board: { w: L.board.w, h: L.board.h },
          hand: { w: L.hand.w, h: L.hand.h, size: L.hand.size, step: L.hand.step },
          trick: { w: L.trick.w, h: L.trick.h, size: L.trick.size },
        })}
        data-composition={L.mode}
        data-tags={tagsKey}
        data-bid-box={L.bid ? rectStr(L.bid) : undefined}
        data-cells={SLOTS.map((s) => `${s}:${rectStr(L.cells[s])}`).join(';')}
        data-your-hand={`${L.hand.x},${L.hand.y},${L.hand.width},${L.hand.h}`}
        data-controls={rectStr(L.ctrl)}
        data-upcard={upcard ? rectStr(L.cells[upcard.slot]) : undefined}
        data-discard-prompt={isMyDealerDiscard ? rectStr(L.ctrl) : undefined}
        data-lifted-card={hoveredCardId ?? ''}
        data-raised-card={raisedCardId ?? ''}
        data-sweeping={sweeping ? 'true' : 'false'}
        style={{ left: boardX, top: L.board.y, width: L.board.w, height: L.board.h }}
      >
        <canvas
          ref={canvasRef}
          id="networked-game-canvas"
          width={backingSize(L.board.w, L.board.h, dpr).width}
          height={backingSize(L.board.w, L.board.h, dpr).height}
          role="img"
          aria-label={view.gameState ? describeAriaLabel(view.gameState, { seatLabels: plainLabels }) : 'Loading game'}
          data-seat-labels={view.gameState ? JSON.stringify(seatLabels) : '{}'}
          style={{ width: `${L.board.w}px`, height: `${L.board.h}px` }}
        />
        {L.status ? statusRow : null}
        {snap && SLOTS.map((s) => {
          const lab = cellLabel(tags[s].you ? 'You' : tags[s].fullName, L.cells[s].w, passed.has(bySlot[s]));
          if (filled.has(s)) return null;
          const c = L.cells[s];
          return (
            <span key={s} class="cell-label" data-slot={s} data-name={tags[s].you ? 'You' : tags[s].fullName} style={{ left: c.x, top: c.y, width: c.w, height: c.h }}>
              <span class="cell-name">{lab.text}</span>
              {lab.pass && <span class="pass">PASS</span>}
            </span>
          );
        })}
        {snap && SLOTS.map((s) => (
          <SeatTag
            key={s}
            slot={s}
            rect={L.tags[s]}
            content={tags[s]}
            active={activeSlot === s}
            team={teamOfSeatLua(bySlot[s]) === myTeam ? 'US' : 'THEM'}
            trumpSuit={trump}
            timer={activeSlot === s && turnDeadline != null
              ? { deadlineMs: turnDeadline, durationMs: view.timerDurationMs, clockOffsetMs, reduced: reducedMotion }
              : null}
          />
        ))}
        {winCard && (
          <WinBadge
            rect={L.cells[slotOfSeat(/** @type {number} */ (winnerSeat), viewerSeat) ?? 'S']}
            team={teamOfSeatLua(/** @type {number} */ (winnerSeat)) === myTeam ? 'US' : 'THEM'}
            cardId={winCard.card_id}
          />
        )}
        {handRects.map((r, i) => {
          const cardId = hand[i];
          const legal = legalSet === null ? true : legalSet.has(cardId);
          const { interactive, legal: hitLegal } = handCardHitState({
            // Frozen while the socket is down, a move waits for its
            // answer or a trick clears; handleCardClick also guards.
            locked: movesLocked || trickClearing, isTrickPlay, isMyTurn: view.isMyTurn, isMyDealerDiscard,
            legalPlays: view.legalPlays, hit: { card_id: cardId, legal },
          });
          const shaking = illegalTap?.card_id === cardId;
          return (
            <button
              key={shaking ? `${cardId}:${illegalTap?.nonce}` : cardId}
              type="button"
              class={shaking ? 'hit-zone hit-zone--shake' : 'hit-zone'}
              data-card-id={cardId}
              data-legal={hitLegal ? 'true' : 'false'}
              aria-label={(isTrickPlay && view.isMyTurn) || isMyDealerDiscard
                ? `${describeCard(cardId)}, ${hitLegal ? 'playable' : 'not playable'}`
                : describeCard(cardId)}
              aria-disabled={!interactive}
              onMouseEnter={() => { if (interactive) setHoveredCardId(cardId); }}
              onMouseLeave={() => setHoveredCardId(null)}
              onClick={() => handleCardClick({ card_id: cardId, legal, x: r.x, y: r.y, w: r.w, h: r.h })}
              style={{ left: r.x, top: r.y, width: r.w, height: r.h, zIndex: 2 + i, cursor: interactive ? 'pointer' : 'default' }}
            />
          );
        })}
        {isMyDealerDiscard && upcard && (() => {
          const r = L.cells[upcard.slot];
          const up = upcardHitState({ locked: movesLocked });
          return (
            <button
              key={`upcard:${upcard.card_id}`}
              type="button"
              class="hit-zone"
              data-testid="upcard-hit-zone"
              data-card-id={upcard.card_id}
              data-upcard="true"
              data-legal={up.legal ? 'true' : 'false'}
              aria-label={`${describeCard(upcard.card_id)}, ${up.legal ? 'playable' : 'not playable'}`}
              aria-disabled={!up.interactive}
              onClick={() => { if (!movesLocked) setDiscardSelection(upcard.card_id); }}
              style={{ left: r.x, top: r.y, width: r.w, height: r.h, cursor: up.interactive ? 'pointer' : 'default' }}
            />
          );
        })()}
        {bidOn && view.gameState ? (
          <CreamBidControls
            L={L}
            state={view.gameState}
            seat={viewerSeat}
            onBid={handleBid}
            seatLabels={plainLabels}
            disabledReason={lockText}
            sending={sending}
          />
        ) : isMyDealerDiscard ? (
          <CreamDiscardPrompt
            rect={L.ctrl}
            selectedCardId={discardSelection}
            onConfirm={handleConfirmDiscard}
            disabledReason={lockText}
            sending={sending}
            pickup={dealerPickupCaption(snap, /** @type {1|2|3|4} */ (viewerSeat), plainLabels)}
          />
        ) : snap ? (
          <PlayControls
            rect={L.ctrl}
            hint={hint?.text ?? ''}
            hintTestId={hint?.testid}
            sending={!!hint?.sending}
            last={last}
            names={plainLabels}
            myTeam={myTeam}
            showLastTrick={showLastTrick}
            dpr={dpr}
          />
        ) : null}
        <TrumpAnnounce
          event={trickHasCards ? null : trumpEvent}
          L={L}
          avoidSlot={isDealerDiscard ? dealerSlot : null}
          makerLine={trumpEvent && !trickHasCards ? makerLineFor(trumpEvent, plainLabels) : ''}
          holdMs={SPLASH_HOLD_MS}
          fadeMs={SPLASH_FADE_MS}
          onDismiss={() => setTrumpEvent(null)}
        />
      </div>

      <div class="cream-notices" style={{ left: boardX + 8, top: L.board.y + 8, width: L.board.w - 16 }}>
        {connectionLost && (
          <div class="networked-conn-banner" data-testid="networked-conn-banner">
            {/* Item 7 (round 3): the drop is announced once. */}
            <span class="networked-conn-banner-announce" role="alert" data-testid="networked-conn-banner-alert">Connection lost.</span>
            {connectErrorText ? (
              <span data-testid="networked-conn-error-text" aria-live="off">{connectErrorText}</span>
            ) : (
              <span data-testid="networked-conn-banner-text" aria-live="off">{reconnectBannerText({ ...service?.getReconnectInfo?.(), phase: connectionPhase ?? undefined })}</span>
            )}{' '}
            {connectErrorText ? (
              <button type="button" class="btn" data-testid="networked-conn-reload" onClick={() => window.location.reload()}>Reload</button>
            ) : (
              <button
                type="button"
                class="btn"
                data-testid="networked-conn-reconnect"
                disabled={connectionPhase === 'connecting'}
                onClick={() => service?.reconnectNow?.()}
              >
                Reconnect now
              </button>
            )}
          </div>
        )}
        {ownSeatBotNote(view.gameState, mySeatIndex) && <div class="networked-bot-note" role="status" data-testid="own-seat-bot-note">{ownSeatBotNote(view.gameState, mySeatIndex)}</div>}
        {view.gameEndedReason === 'engine_failure' && (
          <div class="networked-conn-banner" role="alert" data-testid="networked-engine-failure-banner">
            The game hit a server error and had to end — sorry. Everyone is back in the lobby.
          </div>
        )}
        {confirmLeave && !view.gameOver && (
          <div
            class="networked-leave-confirm"
            role="dialog"
            aria-label="Leave game"
            data-testid="networked-leave-confirm"
            ref={leaveConfirmRef}
          >
            <p>{leaveConfirmText()}</p>
            <div class="notice-buttons">
              <button type="button" class="btn" data-action="leave-confirm" onClick={() => leaveOnce?.()}>Leave</button>
              <button type="button" class="btn btn-primary" data-action="leave-stay" onClick={stayInGame}>Stay</button>
            </div>
          </div>
        )}
        {!view.gameOver && view.passedOut && (
          /* B2-7: everyone passed twice; the countdown + Continue move the deal on. */
          <div class="networked-passed-out networked-bot-note" data-testid="passed-out-panel" role="dialog" aria-label="Hand passed out">
            <p data-testid="passed-out-message">Everyone passed. The deal moves on.</p>
            <ReadyCountdown deadlineMs={view.readyDeadlineMs} clockOffsetMs={clockOffsetMs} />
            <button
              type="button"
              class="btn btn-primary"
              data-action="continue"
              disabled={readyButton.locked}
              aria-disabled={readyButton.locked}
              onClick={() => { if (!readyButton.locked) handleHandReady(); }}
            >
              {readyButton.label ?? 'Continue'}
            </button>
          </div>
        )}
      </div>

      {/* The log stays mounted (hidden while closed), so its lines keep
          their place and a reload or a reopen shows the same list. */}
      <div class="cream-log log-sheet" data-testid={logOpen ? 'log-sheet' : 'log-sheet-closed'} hidden={!logOpen} style={logBox}>
        {logOpen && (
          <div class="log-sheet-head">
            <span class="log-sheet-title">GAME LOG</span>
            <button
              type="button"
              class="btn log-sheet-toggle"
              data-testid="log-sheet-toggle"
              aria-expanded="true"
              onClick={closeLog}
            >
              Close log
            </button>
          </div>
        )}
        <EventLogPanel lines={view.eventLog} />
      </div>

      {/* B2-8: the final recap owns the screen. */}
      {view.gameOver ? (
        <GameOverOverlay
          open
          tableLayout={/** @type {any} */ (gameOverLayout)}
          tableState={view.gameState}
          stageRef={stageRef}
          summary={null}
          scores={view.finalScores}
          winningTeam={finalWinner}
          onPlayAgain={() => {}}
          viewerTeam={viewerTeam}
          target={score.target}
          tableMode={L.mode}
          onAcknowledge={handleHandReady}
          deadlineMs={view.readyDeadlineMs}
          clockOffsetMs={clockOffsetMs}
          waiting={readyButton.locked}
          waitingText={readyButton.label}
        />
      ) : view.passedOut ? null : recapOpen ? (
        <EndOfHandModal
          open
          summary={recapSummary}
          tricks={Array.isArray(view.gameState.completed_tricks)
            ? view.gameState.completed_tricks
            : Object.values(view.gameState.completed_tricks || {})}
          trumpSuit={view.gameState.trump_suit}
          showPartnerReveal={false}
          myTeam={myTeam}
          seatLabels={plainLabels}
          handNumber={handNumber(view.gameState)}
          target={score.target}
          makerSeat={view.gameState.maker_seat ?? null}
          tableMode={L.mode}
          onContinue={handleHandReady}
          waiting={readyButton.locked}
          waitingText={readyButton.label}
          deadlineMs={view.readyDeadlineMs}
          clockOffsetMs={clockOffsetMs}
        />
      ) : null}
    </section>
  );
}
